import bcClient from '../businessCentralClient';
import { BC_CONFIG } from '../config';

/**
 * Timesheet Details API Service
 * Handles all timesheet-related API calls using Business Central API
 * API Group: payroll, Publisher: dogma, Version: v2.0
 */

/**
 * Build the base endpoint for timesheet API
 * @returns {string} Base endpoint path
 */
function getBaseEndpoint() {
  return `/v2.0/${BC_CONFIG.tenantId}/${BC_CONFIG.environment}/api/dogma/payroll/v2.0/companies(${BC_CONFIG.companyId})/timesheetDetails`;
}

/**
 * Build the base endpoint for fiscal years API
 * @returns {string} Base endpoint path
 */
function getFiscalYearsEndpoint() {
  return `/v2.0/${BC_CONFIG.tenantId}/${BC_CONFIG.environment}/api/dogma/payroll/v2.0/companies(${BC_CONFIG.companyId})/slgFiscalYears`;
}

/**
 * Build query string from parameters
 * @param {Object} params - Query parameters
 * @returns {string} Query string
 */
function buildQueryString(params) {
  if (!params || Object.keys(params).length === 0) {
    return '';
  }

  const queryParts = [];
  for (const [key, value] of Object.entries(params)) {
    queryParts.push(`${key}=${encodeURIComponent(value)}`);
  }

  return `?${queryParts.join('&')}`;
}

/**
 * Prepare timesheet data for API submission
 * Keep decimals as numbers (as per API documentation)
 * @param {Object} data - Timesheet data
 * @returns {Object} Cleaned data
 */
function prepareTimesheetData(data) {
  const prepared = {};

  // Process each field in the input data
  Object.keys(data).forEach((key) => {
    const value = data[key];

    // Skip undefined, null, or empty string values
    if (value === undefined || value === null || value === '') {
      return;
    }

    // Keep values as-is (numbers stay as numbers, per API documentation)
    prepared[key] = value;
  });

  console.log('🔄 Data preparation (matching API documentation format):');
  console.log('   Input:', data);
  console.log('   Output:', prepared);
  console.log('   Decimal types:', ['amount', 'rate', 'noOfUnits'].map(f =>
    prepared[f] !== undefined ? `${f}: ${typeof prepared[f]} = ${prepared[f]}` : null
  ).filter(Boolean).join(', '));

  return prepared;
}

const timesheetService = {
  /**
   * Get all timesheet details
   * @param {Object} params - Query parameters
   * @param {string} params.$filter - OData filter query
   * @param {string} params.$select - OData select fields
   * @param {string} params.$orderby - OData orderby clause
   * @param {number} params.$top - Limit number of results
   * @param {number} params.$skip - Skip number of results
   * @returns {Promise} Response with timesheet data
   */
  async getTimesheetDetails(params = {}) {
    console.log('🔍 Fetching timesheet details with params:', params);

    try {
      // Get BC token
      const tokenData = await bcClient.getToken();
      const token = tokenData.access_token;

      // Build endpoint with query params
      const baseEndpoint = getBaseEndpoint();
      const queryString = buildQueryString(params);
      const endpoint = `${baseEndpoint}${queryString}`;

      console.log('Timesheet Endpoint:', endpoint);

      // Make API call
      const response = await bcClient.get(endpoint, token);

      console.log(`✅ Found ${response?.value?.length || 0} timesheet details`);

      // Normalize response to match expected format
      return {
        data: response || { value: [] },
      };
    } catch (error) {
      console.error('❌ Failed to fetch timesheet details:', error);
      throw error;
    }
  },

  /**
   * Get specific timesheet detail by document number and line number
   * @param {string} documentNo - Timesheet document number
   * @param {number} lineNo - Line item number
   * @returns {Promise} Response with timesheet detail
   */
  async getTimesheetDetail(documentNo, lineNo) {
    console.log(`🔍 Fetching timesheet detail: ${documentNo}, Line: ${lineNo}`);

    try {
      // Get BC token
      const tokenData = await bcClient.getToken();
      const token = tokenData.access_token;

      // Build endpoint for specific record
      const baseEndpoint = getBaseEndpoint();
      const endpoint = `${baseEndpoint}(documentNo='${documentNo}',lineNo=${lineNo})`;

      console.log('Timesheet Detail Endpoint:', endpoint);

      // Make API call
      const response = await bcClient.get(endpoint, token);

      console.log('✅ Timesheet detail found');

      return {
        data: response,
      };
    } catch (error) {
      console.error('❌ Failed to fetch timesheet detail:', error);
      throw error;
    }
  },

  /**
   * Get timesheet details by employee code
   * @param {string} employeeCode - Employee code
   * @returns {Promise} Response with timesheet details
   */
  async getTimesheetsByEmployee(employeeCode) {
    const params = {
      $filter: `employeeCode eq '${employeeCode}'`,
      $orderby: 'startDate desc',
    };

    return this.getTimesheetDetails(params);
  },

  /**
   * Get timesheet details by date range
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @returns {Promise} Response with timesheet details
   */
  async getTimesheetsByDateRange(startDate, endDate) {
    const params = {
      $filter: `startDate ge ${startDate} and endDate le ${endDate}`,
      $orderby: 'startDate desc',
    };

    return this.getTimesheetDetails(params);
  },

  /**
   * Get timesheet details by status
   * @param {string} status - Entry status
   * @returns {Promise} Response with timesheet details
   */
  async getTimesheetsByStatus(status) {
    const params = {
      $filter: `status eq '${status}'`,
      $orderby: 'startDate desc',
    };

    return this.getTimesheetDetails(params);
  },

  /**
   * Create new timesheet detail
   * @param {Object} data - Timesheet detail data
   * @param {string} data.documentNo - Timesheet document number
   * @param {number} data.lineNo - Line item number
   * @param {string} data.employeeCode - Employee code
   * @param {string} data.startDate - Start date
   * @param {string} data.endDate - End date
   * @param {number} data.amount - Amount
   * @param {number} data.rate - Rate
   * @param {number} data.noOfUnits - Number of units
   * @param {string} data.status - Status
   * @returns {Promise} Response with created timesheet detail
   */
  async createTimesheetDetail(data) {
    console.log('📝 Creating timesheet detail:', data);

    try {
      // Get BC token
      console.log('🔑 Fetching BC token...');
      const tokenData = await bcClient.getToken();

      if (!tokenData || !tokenData.access_token) {
        throw new Error('Failed to obtain BC access token');
      }

      const token = tokenData.access_token;
      console.log('✅ BC Token obtained:', token.substring(0, 30) + '...');
      console.log('   Token length:', token.length);
      console.log('   Expires in:', tokenData.expires_in, 'seconds');

      // Build endpoint
      const endpoint = getBaseEndpoint();

      // Prepare data - convert decimals to strings for IEEE754Compatible=true
      const preparedData = prepareTimesheetData(data);

      console.log('📤 Create Timesheet Endpoint:', endpoint);
      console.log('📦 Prepared Data (matching API docs):', preparedData);
      console.log('   amount:', preparedData.amount, '(type:', typeof preparedData.amount + ')');
      console.log('   rate:', preparedData.rate, '(type:', typeof preparedData.rate + ')');
      console.log('   noOfUnits:', preparedData.noOfUnits, '(type:', typeof preparedData.noOfUnits + ')');

      console.log('🌐 Making POST request with BC token (no IEEE754Compatible header)...');

      // Make API call WITHOUT IEEE754Compatible header - use default (matching documentation)
      const response = await bcClient.post(endpoint, token, preparedData);

      console.log('✅ Timesheet detail created successfully');

      return {
        data: response,
      };
    } catch (error) {
      console.error('❌ Failed to create timesheet detail');
      console.error('   Error:', error);
      console.error('   Message:', error.message);

      if (error.message && error.message.includes('credentials')) {
        console.error('   🔐 Authentication Error: BC token may be invalid or expired');
        console.error('   Try refreshing the page to get a new token');
      }

      throw error;
    }
  },

  /**
   * Update existing timesheet detail
   * @param {string} documentNo - Timesheet document number
   * @param {number} lineNo - Line item number
   * @param {Object} data - Updated timesheet data
   * @returns {Promise} Response with updated timesheet detail
   */
  async updateTimesheetDetail(documentNo, lineNo, data) {
    console.log(`📝 Updating timesheet detail: ${documentNo}, Line: ${lineNo}`, data);

    try {
      // Get BC token
      const tokenData = await bcClient.getToken();
      const token = tokenData.access_token;

      console.log('🔑 BC Token obtained:', token ? 'Yes' : 'No');

      // Build endpoint for specific record
      const baseEndpoint = getBaseEndpoint();
      const endpoint = `${baseEndpoint}(documentNo='${documentNo}',lineNo=${lineNo})`;

      // Prepare data - convert decimals to strings for IEEE754Compatible=true
      const preparedData = prepareTimesheetData(data);

      console.log('📤 Update Timesheet Endpoint:', endpoint);
      console.log('📦 Prepared Data (matching API docs):', preparedData);

      console.log('🌐 Making PATCH request with BC token (no IEEE754Compatible header)...');

      // Make API call WITHOUT IEEE754Compatible header - use default (matching documentation)
      const response = await bcClient.patch(endpoint, token, preparedData);

      console.log('✅ Timesheet detail updated successfully');

      return {
        data: response,
      };
    } catch (error) {
      console.error('❌ Failed to update timesheet detail:', error);
      throw error;
    }
  },

  /**
   * Delete timesheet detail
   * @param {string} documentNo - Timesheet document number
   * @param {number} lineNo - Line item number
   * @returns {Promise} Response
   */
  async deleteTimesheetDetail(documentNo, lineNo) {
    console.log(`🗑️ Deleting timesheet detail: ${documentNo}, Line: ${lineNo}`);

    try {
      // Get BC token
      const tokenData = await bcClient.getToken();
      const token = tokenData.access_token;

      // Build endpoint for specific record
      const baseEndpoint = getBaseEndpoint();
      const endpoint = `${baseEndpoint}(documentNo='${documentNo}',lineNo=${lineNo})`;

      console.log('Delete Timesheet Endpoint:', endpoint);

      // Make API call
      const response = await bcClient.delete(endpoint, token);

      console.log('✅ Timesheet detail deleted successfully');

      return {
        data: response,
      };
    } catch (error) {
      console.error('❌ Failed to delete timesheet detail:', error);
      throw error;
    }
  },

  /**
   * Calculate total hours/units for a period
   * @param {Array} timesheetDetails - Array of timesheet details
   * @returns {Object} Summary with totals
   */
  calculateTotals(timesheetDetails) {
    const totals = {
      totalUnits: 0,
      totalAmount: 0,
      count: timesheetDetails.length,
    };

    timesheetDetails.forEach((detail) => {
      totals.totalUnits += detail.noOfUnits || 0;
      totals.totalAmount += detail.amount || 0;
    });

    return totals;
  },

  /**
   * Get all fiscal years
   * @returns {Promise} Response with fiscal years data
   */
  async getFiscalYears() {
    console.log('🔍 Fetching fiscal years');

    try {
      // Get BC token
      const tokenData = await bcClient.getToken();
      const token = tokenData.access_token;

      // Build endpoint
      const endpoint = getFiscalYearsEndpoint();

      console.log('Fiscal Years Endpoint:', endpoint);

      // Make API call
      const response = await bcClient.get(endpoint, token);

      console.log(`✅ Found ${response?.value?.length || 0} fiscal years`);

      return {
        data: response || { value: [] },
      };
    } catch (error) {
      console.error('❌ Failed to fetch fiscal years:', error);
      throw error;
    }
  },

  /**
   * Get timesheet details with fiscal year filter
   * @param {string} fiscalYear - Fiscal year (e.g., '2026/27')
   * @param {string} employeeCode - Employee code (optional)
   * @returns {Promise} Response with timesheet details data
   */
  async getTimesheetDetailsByFiscalYear(fiscalYear, employeeCode = null) {
    console.log('🔍 Fetching timesheet details for fiscal year:', fiscalYear);

    try {
      // Get BC token
      const tokenData = await bcClient.getToken();
      const token = tokenData.access_token;

      // Build filter - always include status eq 'Released'
      let filter = `fiscalYear eq '${fiscalYear}' and status eq 'Released'`;

      // Add employee filter if provided
      if (employeeCode) {
        filter += ` and employeeCode eq '${employeeCode}'`;
      }

      // Build endpoint with filter
      const endpoint = `${getBaseEndpoint()}?$filter=${encodeURIComponent(filter)}`;

      console.log('Timesheet Details Endpoint:', endpoint);

      // Make API call
      const response = await bcClient.get(endpoint, token);

      console.log(`✅ Found ${response?.value?.length || 0} timesheet details`);

      return {
        data: response || { value: [] },
      };
    } catch (error) {
      console.error('❌ Failed to fetch timesheet details:', error);
      throw error;
    }
  },
};

export default timesheetService;
